import { Building2, Clock, MapPin, Search, TrendingUp, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SearchSuggestion, SearchSuggestionKind } from '@/lib/searchSuggestions'

const KIND_ICONS: Record<SearchSuggestionKind, typeof Search> = {
  project: Building2,
  property: Building2,
  locality: MapPin,
  builder: Building2,
  query: TrendingUp,
  recent: Clock,
}

interface SearchSuggestionsDropdownProps {
  open: boolean
  suggestions: SearchSuggestion[]
  query: string
  activeIndex: number
  onSelect: (suggestion: SearchSuggestion) => void
  onHoverIndex: (index: number) => void
  className?: string
  variant?: 'dropdown' | 'inline'
}

export function SearchSuggestionsDropdown({
  open,
  suggestions,
  query,
  activeIndex,
  onSelect,
  onHoverIndex,
  className,
  variant = 'dropdown',
}: SearchSuggestionsDropdownProps) {
  if (!open || suggestions.length === 0) return null

  const isInline = variant === 'inline'

  return (
    <div
      className={cn(
        isInline
          ? 'min-h-0 flex-1 overflow-hidden bg-white'
          : 'absolute left-0 right-0 top-full z-50 rounded-b-xl border border-neutral-200 border-t-0 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.12)]',
        className,
      )}
    >
      <ul
        id="search-suggestions"
        aria-label="Search suggestions"
        className={cn(
          'hide-scrollbar',
          isInline ? 'h-full overflow-y-auto' : 'max-h-[320px] overflow-y-auto rounded-b-xl py-0.5',
        )}
      >
        {suggestions.map((suggestion, index) => {
          const Icon = KIND_ICONS[suggestion.kind]
          const active = index === activeIndex

          if (isInline) {
            return (
              <li key={suggestion.id}>
                <button
                  type="button"
                  onMouseEnter={() => onHoverIndex(index)}
                  onClick={() => onSelect(suggestion)}
                  className={cn(
                    'flex w-full items-center gap-2.5 border-b border-neutral-100 px-4 py-2.5 text-left transition-colors',
                    active ? 'bg-primary-50/60' : 'hover:bg-neutral-50',
                  )}
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-neutral-50 text-neutral-400">
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium leading-snug text-neutral-800">
                      {highlightMatch(suggestion.title, query)}
                    </span>
                    {(suggestion.subtitle || suggestion.kindLabel) && (
                      <span className="mt-0.5 block truncate text-[11px] leading-tight text-neutral-500">
                        {suggestion.subtitle ?? suggestion.kindLabel}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            )
          }

          return (
            <li key={suggestion.id}>
              <button
                type="button"
                onMouseEnter={() => onHoverIndex(index)}
                onClick={() => onSelect(suggestion)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left transition-colors',
                  active ? 'bg-primary-50' : 'hover:bg-neutral-50',
                )}
              >
                <span
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                    active ? 'bg-primary-100 text-primary-600' : 'bg-neutral-100 text-neutral-500',
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium leading-tight text-neutral-900">
                    {highlightMatch(suggestion.title, query)}
                  </span>
                  {suggestion.subtitle && (
                    <span className="mt-0.5 block truncate text-[11px] leading-tight text-neutral-500">
                      {suggestion.subtitle}
                    </span>
                  )}
                </span>

                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                  {suggestion.kindLabel}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function highlightMatch(text: string, query: string) {
  const trimmed = query.trim()
  if (!trimmed) return text

  const lowerText = text.toLowerCase()
  const lowerQuery = trimmed.toLowerCase()
  const index = lowerText.indexOf(lowerQuery)
  if (index === -1) return text

  return (
    <>
      {text.slice(0, index)}
      <mark className="rounded bg-transparent font-semibold text-neutral-900">
        {text.slice(index, index + trimmed.length)}
      </mark>
      {text.slice(index + trimmed.length)}
    </>
  )
}
